// The four canvas views (Frontend · Infra · Tasks · Agents) — single source of
// truth lives in CanvasViewNav.js (the left-rail switcher). The More dropdown
// mirrors them as a VIEWS section below, mapping `path` → the dropdown's `route`
// item shape rather than re-typing the routes here.
import { VIEW_NAV } from './CanvasViewNav.js'
import { stripWorkspacePrefix, canonicalRoutePath } from '../functions/workspacePrefix.js'
import { navItemVisible } from '../functions/featureFlags.js'
import { visibleOrgs } from '../functions/visibleOrgs.js'

// Dropdown mirrors the full set above plus the workspace-home surfaces
// that don't have a dedicated navbar slot (My / Announcements / Dashboard /
// Data). Split into Hr-separated sections so the menu reads as grouped
// chrome. Each `divider: true` row tells AppNavbarMoreMenu to render an
// Hr instead of an AppNavbarDropdownItem. Section order:
//   1. Workspace-home surfaces (Dashboard / My / Announcements)
//   2. Workspace-content apps (Studio · Marketplace · People · Tickets ·
//      Documents · Logs · Data)
//   3. Chrome apps (Calendar · Chat · Meet · Company · System Status)
//   4. Creator surface (Add app)
//   5. VIEWS — the four canvas lenses (Frontend · Infra · Tasks · Agents),
//      mirrored from CanvasViewNav's VIEW_NAV (the left-rail switcher) so the
//      navbar dropdown offers the same view-switch the sidebar does.
export const DROPDOWN_APPS = [
  { key: 'home', icon: 'logov2', route: '/', label: 'shell.app.workspace' },
  { key: 'my', icon: 'user', route: '/my', label: 'shell.app.myProfile' },
  { key: 'announcements', icon: 'bell', route: '/announcements', label: 'shell.app.announcements' },
  { key: 'div-1', divider: true },
  { key: 'studio', icon: 'layers', route: '/studio', label: 'shell.app.studio' },
  { key: 'infra', icon: 'server', route: '/infra', label: 'shell.app.infra' },
  { key: 'marketplace', icon: 'blocks', route: '/marketplace', label: 'shell.app.marketplace' },
  {
    key: 'teamandagents',
    icon: 'people',
    route: '/team-and-agents',
    label: 'shell.app.teamAndAgents'
  },
  { key: 'agents', icon: 'star', route: '/agents', label: 'shell.app.agents' },
  // `/people` is the flat directory — kept in the dropdown so users
  // searching for the old surface still find it. `/team-and-agents` is
  // the structural lens that owns the navbar slot now.
  { key: 'people', icon: 'user', route: '/team-members', label: 'shell.app.teamMembers' },
  { key: 'tickets', icon: 'check', route: '/tasks', label: 'shell.app.tasks' },
  { key: 'documents', icon: 'document', route: '/notes', label: 'shell.app.notes' },
  { key: 'files', icon: 'folder', route: '/files', label: 'shell.app.files' },
  { key: 'analytics', icon: 'analytics', route: '/analytics', label: 'shell.app.analytics' },
  { key: 'data', icon: 'database', route: '/data/projects', label: 'shell.app.data' },
  { key: 'div-2', divider: true },
  { key: 'calendar', icon: 'date outline', route: '/calendar', label: 'shell.app.calendar' },
  { key: 'reminders', icon: 'clockOutline', route: '/reminders', label: 'shell.app.reminders' },
  // History — the built-in timeline of the workspace activity log.
  { key: 'history', icon: 'sfHistory', route: '/history', label: 'shell.app.history' },
  { key: 'chat', icon: 'chat', route: '/chat', label: 'shell.app.chat' },
  // Mail rides the same `navItemVisible` gate as every row here, so the
  // dark-by-default `mail` flag keeps it out of the menu until it is on.
  { key: 'mail', icon: 'email', route: '/mail', label: 'shell.app.mail' },
  { key: 'meet', icon: 'video', route: '/meet', label: 'shell.app.meet' },
  { key: 'company', icon: 'info', route: '/company', label: 'shell.app.company' },
  { key: 'div-3', divider: true },
  { key: 'addapps', icon: 'plus', route: '/add-app', label: 'shell.app.addApp' },
  { key: 'div-4', divider: true },
  // VIEWS — mapped from CanvasViewNav's VIEW_NAV so the routes stay
  // single-sourced. `path` → the dropdown's `route` item shape; key prefixed
  // `view-` so it can't collide with the app keys above.
  ...VIEW_NAV.map((v) => ({
    key: `view-${v.key}`,
    icon: v.icon,
    route: v.path,
    label: v.label
  }))
]

// Routes that belong to the workspace home surface — used by isIntranetRoute
// to decide whether to show the nav links bar instead of the global search.
// Cross-file export (imported elsewhere) — safe as-is since it's never read
// from within a factory IN THIS FILE (AppNavbarButton's own scope carries
// its own literal copy below — see the scope comment on _isActive's
// replacement for why an in-file bare read of an exported sibling is just as
// unresolvable as a module-local one once frank.toJSON's the component).
export const WORKSPACE_PATHS = [
  '/',
  '/team-members',
  '/people',
  '/my',
  '/announcements',
  '/company',
  '/profile'
]
export const INTRANET_PATHS = WORKSPACE_PATHS

// Row styling mirrors `AppOrgSwitcherItem` (shared/components/
// AppOrgSwitcher.js) so the More dropdown reads as a peer of the org
// switcher menu. Same padding (`Z1 A`), round (`Y`), fontSize (`Z2`),
// fontWeight (`500`), and hover background (`overlay`) — all STATIC
// values. The previous divider-morph (every visual prop a
// `s?.divider ? … : …` factory so one component could render both link
// rows and `<hr>`s) did not apply reliably inside the dropdown plugin's
// Panel mount — rows rendered with `padding: 0` and read as a cramped
// text list. Dividers are static `Divider_N` children on the menu body
// now (same as AppOrgSwitcher.Menu.Divider), so the rows can use plain
// values end-to-end.
export const AppNavbarDropdownItem = {
  // FA204 / frank trap — helpers live in `scope`, not module scope (a bare
  // module-scope OR sibling-exported read silently reads as undefined once
  // the component is frank.toJSON'd for platform delivery — workspace
  // d6ea1142, project_frank_drops_module_local_helpers). `hrefFor`/
  // `resolveStudioPath` are duplicated verbatim in AppNavbarButton's own
  // scope below (a helper used by multiple components in one file gets
  // duplicated into each). `cancelMoreClose`/`moreState` coordinate with
  // AppNavbarButton's More-dropdown timer/open-flag through `el.context` —
  // the one thing genuinely SHARED across components that live in
  // different subtrees (this row renders inside the dropdown portal, not as
  // a descendant of AppNavbarButton) — see the fuller comment on
  // AppNavbarButton.scope.moreState below.
  scope: {
    resolveStudioPath(s) {
      const root = s?.root
      const pendingId = root?._pendingActiveOrgId
      if (pendingId) {
        const pendingOrg = (root?.jwtClaims?.orgs || []).find((o) => o.id === pendingId)
        if (pendingOrg?.slug) return `/studio/${pendingOrg.slug}`
      }
      const orgSlug = root?.org?.slug
      if (orgSlug) return `/studio/${orgSlug}`
      // No active org: the personal studio — unless that org is the empty
      // background one beside a team (functions/visibleOrgs.js, POB round
      // 5); then the team's studio.
      const orgs = root?.jwtClaims?.orgs || []
      const shown = visibleOrgs(orgs)
      const personalOrg = shown.find((o) => o?.type === 'personal')
      if (personalOrg?.slug) return `/studio/${personalOrg.slug}`
      if (shown !== orgs && shown[0]?.slug) return `/studio/${shown[0].slug}`
      return '/studio'
    },
    hrefFor(el, s) {
      if (s?.key === 'studio') return this.resolveStudioPath(s)
      if (!s || !Object.prototype.hasOwnProperty.call(s, 'route')) return undefined
      return s.route || undefined
    },
    btnLabel(s) {
      return s?.label || (s?.key ? s.key.charAt(0).toUpperCase() + s.key.slice(1) : '')
    },
    moreState(el) {
      const ctx = el.context
      if (!ctx.__appNavbarMore) ctx.__appNavbarMore = { timer: null, open: false }
      return ctx.__appNavbarMore
    },
    cancelMoreClose(el) {
      const st = this.moreState(el)
      if (st.timer) {
        clearTimeout(st.timer)
        st.timer = null
      }
    }
  },
  extends: 'Link',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  color: 'title',
  width: '100%',
  textDecoration: 'none',
  // Row chrome = the house MenuPanelItem recipe (kept inline because this
  // already extends Link): Y/Z1 padding, Z round, tokenized hover.
  padding: 'Y Z1',
  round: 'Z',
  border: 'none',
  ':hover': { background: 'hover' },

  href: (el, s) => el.scope.hrefFor(el, s),
  // Set both `title` (native tooltip + status bar) and `aria-label`
  // (screen readers) directly on `attr`. Top-level `aria: { label }`
  // does NOT promote to the `aria-label` attribute in v3.14 — verified
  // against live DOM.
  attr: {
    title: (el, s) => el.call('polyglot', el.scope.btnLabel(s)),
    'aria-label': (el, s) => el.call('polyglot', el.scope.btnLabel(s))
  },

  onClick: (e, el, s) => {
    el.scope.cancelMoreClose(el)
    el.call('closeDropdown', 'appNavbarMore')
    e.preventDefault()
    const route = el.scope.hrefFor(el, s)
    if (!route) return
    // Non-canvas apps + workspace-module pages (marketplace, announcements,
    // documents, calendar, chat, …) open INSIDE the shell PageSlot; canvas
    // surfaces (studio/agents/team-and-agents/tickets/files/views) stay
    // full-page. openAppPage owns that decision — the same idiom the header
    // tabs + apps launcher use. Forward the event so cmd/ctrl-click still arms
    // split view. Guard for hosts where openAppPage isn't registered.
    try {
      // preferSlot: navbar link → non-canvas routes open in the shell PageSlot
      // (module tabs included); canvas routes stay full-page (isCanvasRoute
      // wins). split carries the cmd/ctrl second-pane gesture.
      el.call('openAppPage', route, { preferSlot: true, split: e.metaKey || e.ctrlKey })
      return
    } catch (_) {}
    el.router(route, el.getRoot())
  },

  Icon: { name: '{{icon}}', boxSize: 'A A' },
  Label: { tag: 'span', fontSize: 'Z2', text: (el, s) => el.call('polyglot', s.label || '') }
}

// Body-only menu rendered inside the dropdown plugin's DropdownRoot
// Panel — same chrome as AppOrgSwitcherMenu (the generic dropdown
// look): the Panel supplies theme/round/shadow/maxHeight, the body
// declares the same flow/minWidth/padding/textAlign the org menu uses,
// and sections + static Divider rows replace the legacy per-row
// divider morph. The enter/leave handlers keep the More button's 200ms
// close-debounce alive while the cursor is over the panel.
export const AppNavbarMoreMenu = {
  // FA204 / frank trap — see the header comment on AppNavbarDropdownItem's
  // scope above for the full rationale. `menuSections` (was `_MENU_SECTIONS`)
  // is a plain computed VALUE, not a function body, so it bakes to a literal
  // array at serialization time exactly like ActivityChart's `total` —
  // still homed in scope (not left at module scope) since it's private to
  // this menu, not meant for cross-file consumption the way DROPDOWN_APPS
  // is. `cancelMoreClose`/`moreState` are duplicated verbatim from
  // AppNavbarButton/AppNavbarDropdownItem's own scopes — all three read/write
  // the SAME `el.context.__appNavbarMore` bag, so the shared open/close
  // coordination survives even though this menu, the trigger button, and
  // the dropdown-portaled row all sit in different DOM subtrees (`el.scope`
  // ancestor-inheritance would NOT connect them; `el.context` is the one
  // reference every element in the app shares regardless of mount position).
  scope: {
    menuSections: DROPDOWN_APPS.reduce(
      (acc, item) => {
        if (item.divider) acc.push([])
        else acc[acc.length - 1].push(item)
        return acc
      },
      [[]]
    ),
    moreState(el) {
      const ctx = el.context
      if (!ctx.__appNavbarMore) ctx.__appNavbarMore = { timer: null, open: false }
      return ctx.__appNavbarMore
    },
    cancelMoreClose(el) {
      const st = this.moreState(el)
      if (st.timer) {
        clearTimeout(st.timer)
        st.timer = null
      }
    },
    scheduleMoreClose(el) {
      this.cancelMoreClose(el)
      const st = this.moreState(el)
      st.timer = setTimeout(() => {
        st.timer = null
        el.call('closeDropdown', 'appNavbarMore')
      }, 200)
    }
  },
  extends: 'MenuPanel',
  tag: 'div',
  minWidth: 'G',
  textAlign: 'left',

  onMouseenter: (ev, el) => el.scope.cancelMoreClose(el),
  onMouseleave: (ev, el) => el.scope.scheduleMoreClose(el),

  Section_1: {
    flow: 'y',
    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace'],
    children: (el, s) => el.scope.menuSections[0].filter((it) => navItemVisible(s.root, it))
  },
  Divider_1: { height: '1px', background: 'line', margin: 'Z 0' },
  Section_2: {
    flow: 'y',
    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace'],
    children: (el, s) => el.scope.menuSections[1].filter((it) => navItemVisible(s.root, it))
  },
  Divider_2: { height: '1px', background: 'line', margin: 'Z 0' },
  Section_3: {
    flow: 'y',
    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace'],
    children: (el, s) => el.scope.menuSections[2].filter((it) => navItemVisible(s.root, it))
  },
  Divider_3: { height: '1px', background: 'line', margin: 'Z 0' },
  Section_4: {
    flow: 'y',
    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace'],
    children: (el, s) => el.scope.menuSections[3].filter((it) => navItemVisible(s.root, it))
  },
  Divider_4: { height: '1px', background: 'line', margin: 'Z 0' },
  // VIEWS — the four canvas lenses (Frontend · Infra · Tasks · Agents),
  // mapped from VIEW_NAV into DROPDOWN_APPS above. Same AppNavbarDropdownItem
  // row + static Divider convention as every other section.
  Section_5: {
    flow: 'y',
    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace'],
    children: (el, s) => el.scope.menuSections[4].filter((it) => navItemVisible(s.root, it))
  }
}

export const AppNavbarButton = {
  // FA204 / frank trap — see AppNavbarDropdownItem's scope comment above for
  // the full rationale. `isActive`/`hrefFor`/`btnLabel`/`resolveStudioPath`/
  // `unreadFor` are pure and duplicated here rather than shared with
  // AppNavbarDropdownItem's copies (object-literal scope properties can't
  // reference each other across DIFFERENT top-level components — only
  // within the SAME scope object via `this`). `isActive` carries its OWN
  // literal copy of the workspace-home path list instead of reading the
  // exported `WORKSPACE_PATHS` above — a bare in-file read of a sibling
  // export is exactly as unresolvable post-frank.toJSON as a module-local
  // const (see the readerVoice.js precedent — `readerStop`/
  // `readerBoundaryFor` were both EXPORTED and still broken bare).
  // `moreState`/`cancelMoreClose`/`openMoreDropdown`/`scheduleMoreClose`
  // read/write `el.context.__appNavbarMore` — the one reference every
  // element in the app shares regardless of subtree, which is what lets
  // this button's hover/click state coordinate with AppNavbarMoreMenu (a
  // dropdown-portaled sibling, not a DOM descendant) and
  // AppNavbarDropdownItem (same portal) without a shared module closure.
  scope: {
    isActive(s) {
      if (!s || !Object.prototype.hasOwnProperty.call(s, 'route')) return false
      const slot = s.root?._canvasSlot
      const path = canonicalRoutePath(
        slot ? String(slot).split('?')[0] : stripWorkspacePrefix(s.root?.currentPath || '/')
      )
      const workspacePaths = [
        '/',
        '/team-members',
        '/people',
        '/my',
        '/announcements',
        '/company',
        '/profile'
      ]
      return s.route === '/' ? workspacePaths.includes(path) : path.startsWith(s.route)
    },
    resolveStudioPath(s) {
      const root = s?.root
      const pendingId = root?._pendingActiveOrgId
      if (pendingId) {
        const pendingOrg = (root?.jwtClaims?.orgs || []).find((o) => o.id === pendingId)
        if (pendingOrg?.slug) return `/studio/${pendingOrg.slug}`
      }
      const orgSlug = root?.org?.slug
      if (orgSlug) return `/studio/${orgSlug}`
      // No active org: the personal studio — unless that org is the empty
      // background one beside a team (functions/visibleOrgs.js, POB round
      // 5); then the team's studio.
      const orgs = root?.jwtClaims?.orgs || []
      const shown = visibleOrgs(orgs)
      const personalOrg = shown.find((o) => o?.type === 'personal')
      if (personalOrg?.slug) return `/studio/${personalOrg.slug}`
      if (shown !== orgs && shown[0]?.slug) return `/studio/${shown[0].slug}`
      return '/studio'
    },
    hrefFor(el, s) {
      if (s?.key === 'studio') return this.resolveStudioPath(s)
      if (!s || !Object.prototype.hasOwnProperty.call(s, 'route')) return undefined
      return s.route || undefined
    },
    btnLabel(s) {
      return s?.label || (s?.key ? s.key.charAt(0).toUpperCase() + s.key.slice(1) : '')
    },
    unreadFor(s) {
      return Number(s?.root?.navUnreadCounts?.[s?.key] || 0)
    },
    moreState(el) {
      const ctx = el.context
      if (!ctx.__appNavbarMore) ctx.__appNavbarMore = { timer: null, open: false }
      return ctx.__appNavbarMore
    },
    cancelMoreClose(el) {
      const st = this.moreState(el)
      if (st.timer) {
        clearTimeout(st.timer)
        st.timer = null
      }
    },
    openMoreDropdown(el) {
      this.cancelMoreClose(el)
      const st = this.moreState(el)
      if (st.open) return
      el.call('openDropdown', {
        anchorEl: el,
        id: 'appNavbarMore',
        placement: 'bottom-end',
        // ORGSWITCHER-PANEL-SURFACE-1 sweep: the body (AppNavbarMoreMenu)
        // extends MenuPanel and carries `radiusCard`, so the plugin shell
        // takes the same radius and never shows its own corners behind the
        // card. Width stays where it belongs — on the body's `minWidth: 'G'`.
        round: 'radiusCard',
        content: { extends: 'AppNavbarMoreMenu' },
        onOpen: () => {
          st.open = true
        },
        onClose: () => {
          st.open = false
        }
      })
    },
    scheduleMoreClose(el) {
      this.cancelMoreClose(el)
      const st = this.moreState(el)
      st.timer = setTimeout(() => {
        st.timer = null
        el.call('closeDropdown', 'appNavbarMore')
      }, 200)
    }
  },
  // Multi-extends: NavbarButton supplies the icon-rendering chain
  // (IconText → Icon child resolved from `icon: '{{ icon }}'`) plus
  // hover tooltip wiring; Link supplies tag: 'a' + attr.href so the
  // button renders as a real anchor with native middle-click + status
  // bar URL. Custom onClick below overrides Link's routerOnClick.
  extends: ['NavbarButton', 'Link'],
  borderWidth: '0',
  round: '100%',
  position: 'relative',
  cursor: 'pointer',
  textDecoration: 'none',
  // Active state is driven by reactive factories instead of `isActive` +
  // `.isActive` because smbls v3.14 only wires reactive effects for
  // function props that are in the CSS_PROPS_REGISTRY. `isActive` is a
  // custom boolean, so `applyConditionals` runs the check ONCE at
  // creation and freezes it. `background` and `color` ARE in the CSS
  // registry, so their factories re-run whenever `root.currentPath`
  // updates via `locationchange`.
  //
  // Theme-aware tokens: the `selected` wash (active) for the resolved
  // active background. Hover bg uses explicit `@light`/`@dark` blocks below
  // because the `overlay` token (`gray14.05` light / `gray2.05` dark) is
  // designed for translucent modal-style overlays — on the navbar's
  // light `gray15.95` background it rendered as a near-invisible muddy
  // gray patch that read as "broken" rather than "hovered" (#2206).
  //
  // The hover values mirror the brand `navbarButton` theme's own
  // `:hover` recipe (`gray13.95` light / `gray4.9` dark) so the
  // affordance matches every other navbar button surface in the system
  // — we can't just rely on the inherited theme because this component
  // sets `background` as a reactive factory (needed for the active-
  // state factory to re-evaluate on route change), and a factory at the
  // base level overrides the theme's base bg, which in turn means the
  // theme's `:hover` block alone wouldn't supply a hover bg either.
  // Setting `:hover` here with `@light`/`@dark` keeps both states
  // theme-aware end-to-end without fighting the factory.
  //
  // Color contrast — both active and inactive icons use `title`. The
  // active/inactive distinction is carried by `background` (`selected`
  // vs transparent), not by fading the icon glyph itself. Previously
  // inactive icons used `caption` (gray3 light / gray13 dark) which is a
  // secondary-text token; on the navbar's `gray15.95` light bg the glyphs
  // fell to ~3.4:1 contrast through the SVG sprite stroke, well below
  // the 7:1 we want for top-level chrome. `title` (gray1 light / gray15
  // dark) keeps the icons crisp in either theme without fighting the
  // brand `navbarButton` theme's own @light/@dark color rules.
  background: (el, s) => (el.scope.isActive(s) ? 'selected' : 'transparent'),
  color: 'title',
  ':hover': {
    color: 'title',
    '@light': { background: 'gray13.95' },
    '@dark': { background: 'gray4.9' }
  },

  href: (el, s) => el.scope.hrefFor(el, s),
  // Custom navbar tooltip uses setNavbarTooltipPosition (onMouseover);
  // we still want the native title + aria-label for accessibility +
  // middle-click status bar. el.text is empty (icon-only) — top-level
  // `aria: { label }` does NOT promote to `aria-label` in v3.14, so
  // both must live on `attr`.
  attr: {
    title: (el, s) => el.call('polyglot', el.scope.btnLabel(s)),
    'aria-label': (el, s) => el.call('polyglot', el.scope.btnLabel(s))
  },
  icon: '{{ icon }}',
  // SIZE-1 item 3 (tickets/sonnet.md, WAVE-G, 2026-08-14/15): every icon in
  // this row — including the Logo instance, MainNavbar.js's first child —
  // measured 22×22, just under the 24px comfortable-minimum floor. Every
  // MainNavbar icon shares this one component (no per-instance override
  // exists anywhere in MainNavbar.js), so raising the floor here fixes the
  // whole row uniformly instead of making the Logo alone a mismatched size
  // against its siblings. Literal `width`/`height` (NOT the `boxSize`
  // shorthand other Icon overrides in this file use) — the framework's own
  // base Icon component sets literal `width`/`height` directly, and mixing
  // a shorthand with its own longhand on one element is order-undefined in
  // the atomic css-in-props engine (the exact bug class already found +
  // fixed on the /reminders CheckIcon this same sweep, workspace 45a50437).
  // Same keys as the base → DOMQL's child-overrides-parent extends
  // precedence resolves it unambiguously.
  Icon: { width: 'iconLg', height: 'iconLg' },

  // The More button opens its dropdown on HOVER. Open fires immediately
  // on mouseenter; close is debounced 200ms on mouseleave so the cursor
  // can travel from the trigger down into the dropdown without it
  // snapping shut mid-traverse. The dropdown panel itself mirrors the
  // same enter/leave handlers (in AppHeader.js) so once the cursor is
  // over the panel the timer is canceled and re-armed on exit.
  onMouseenter: (ev, el, s) => {
    if (s.key === 'more') el.scope.openMoreDropdown(el)
  },
  // Brand's onMouseleave calls Tooltip.update() which can't override
  // the inline styles setNavbarTooltipPosition sets; trigger the
  // slide-out animation directly instead.
  onMouseleave: (ev, el, s) => {
    if (s.hover) {
      s.hover = false
      el.call('setNavbarTooltipPosition', { visible: false })
    }
    if (s.key === 'more') el.scope.scheduleMoreClose(el)
  },

  onClick: (e, el, s) => {
    // Hover already handles open/close for the More button; click still
    // toggles as an accessibility fallback (keyboard + touch users).
    // DropdownRoot's outside-click closer deliberately ignores clicks on
    // the anchor, so the toggle here is the only click path for More.
    if (s.key === 'more') {
      e.preventDefault()
      if (el.scope.moreState(el).open) {
        el.scope.cancelMoreClose(el)
        el.call('closeDropdown', 'appNavbarMore')
      } else {
        el.scope.openMoreDropdown(el)
      }
      return
    }
    e.preventDefault()
    const route = el.scope.hrefFor(el, s)
    if (!route) return
    // Non-canvas navbar links open INSIDE the shell PageSlot; canvas surfaces
    // (studio/tickets/team-and-agents/agents/files/views) stay full-page.
    // openAppPage owns that decision — the SAME idiom the More dropdown items,
    // header tabs and apps launcher already use, so the icon row no longer
    // diverges by hard-navigating every link. Forward the event so cmd/ctrl-
    // click still arms split view. Guard for hosts where openAppPage isn't
    // registered (non-shell mounts of this navbar) → plain routing.
    try {
      el.call('openAppPage', route, { preferSlot: true, split: e.metaKey || e.ctrlKey })
      return
    } catch (_) {}
    el.router(route, el.getRoot())
  },

  UnreadBadge: {
    hide: (el, s) => el.scope.unreadFor(s) === 0,
    position: 'absolute',
    top: '0',
    right: '0',
    width: 'Y',
    height: 'Y',
    round: '50%',
    background: 'red',
    pointerEvents: 'none'
  }
}

export const AppNavbar = {
  // FA204 / frank trap — see AppNavbarDropdownItem's scope comment above for
  // the full rationale. `apps` (was module-scope `APPS`) and the page-title
  // resolver (was module-scope `pageTitleFor`/`_NAVBAR_TITLES`/
  // `_NAVBAR_PREFIX`) are private to this component, so they move into its
  // own scope rather than staying at module scope.
  scope: {
    apps: [
      { key: 'logo', icon: 'logov2', route: '/', label: 'shell.app.workspace' },
      { key: 'studio', icon: 'layers', route: '/studio', label: 'shell.app.studio' },
      { key: 'infra', icon: 'server', route: '/infra', label: 'shell.app.infra' },
      // `blocks` reads as a stacked-lego glyph — Marketplace is the
      // "browse + install apps" surface, distinct from the AddApps creator
      // page which uses the bare `plus` icon below.
      { key: 'marketplace', icon: 'blocks', route: '/marketplace', label: 'shell.app.marketplace' },
      { key: 'people', icon: 'people', route: '/team-members', label: 'shell.app.teamMembers' },
      // Tickets→Tasks rename (2026-08-06 per Nika) — internal key stays
      // 'tickets' (prefs/flag identity), the route + label are the
      // canonical user-facing form.
      { key: 'tickets', icon: 'check', route: '/tasks', label: 'shell.app.tasks' },
      { key: 'documents', icon: 'document', route: '/notes', label: 'shell.app.notes' },
      { key: 'files', icon: 'folder', route: '/files', label: 'shell.app.files' },
      { key: 'analytics', icon: 'analytics', route: '/analytics', label: 'shell.app.analytics' },
      { key: 'calendar', icon: 'date outline', route: '/calendar', label: 'shell.app.calendar' },
      { key: 'reminders', icon: 'clockOutline', route: '/reminders', label: 'shell.app.reminders' },
      // History — the built-in timeline of the workspace activity log.
      { key: 'history', icon: 'sfHistory', route: '/history', label: 'shell.app.history' },
      // "Team chat" (not just "Chat") differentiates from the header's
      // Messenger button ("Direct messages") — workspace-bug #2184. Glyph
      // stays `chat` (multi-dot speech-bubble = group chat); Messenger uses
      // `sfMessage` (single bubble = 1:1 DM).
      { key: 'chat', icon: 'chat', route: '/chat', label: 'shell.app.chat' },
      // Mail — hidden until the `mail` flag is on; the row list below is
      // filtered by navItemVisible, which resolves '/mail' → the flag.
      { key: 'mail', icon: 'email', route: '/mail', label: 'shell.app.mail' },
      { key: 'meet', icon: 'video', route: '/meet', label: 'shell.app.meet' },
      { key: 'company', icon: 'info', route: '/company', label: 'shell.app.company' },
      { key: 'addapps', icon: 'plus', route: '/add-app', label: 'shell.app.addApp' },
      { key: 'more', icon: 'arrowAngleDown', label: 'shell.app.more' }
    ],
    // Small page-title resolver — matches AppHeader's pageTitle() so the
    // label stays consistent now that PageTitle is rendered here instead.
    NAVBAR_TITLES: {
      '/calendar': 'shell.app.calendar',
      '/reminders': 'shell.app.reminders',
      '/history': 'shell.app.history',
      '/inbox': 'shell.pageTitle.inbox',
      '/chat': 'shell.app.chat',
      '/mail': 'shell.app.mail',
      // Canonical /tasks keys + legacy /tickets aliases (tickets→tasks
      // rename 2026-08-06) — the alias URLs stay reachable, so both key
      // sets label.
      '/tasks': 'shell.pageTitle.myView',
      '/tasks/my': 'shell.pageTitle.myView',
      '/tasks/general': 'shell.pageTitle.general',
      '/tasks/feed': 'shell.pageTitle.feed',
      '/tasks/canvas': 'shell.pageTitle.canvas',
      '/tasks/stories': 'shell.pageTitle.stories',
      '/tasks/epics': 'shell.pageTitle.epics',
      '/tasks/board': 'shell.pageTitle.board',
      '/tasks/standups': 'shell.pageTitle.standups',
      '/tickets': 'shell.pageTitle.myView',
      '/tickets/my': 'shell.pageTitle.myView',
      '/tickets/general': 'shell.pageTitle.general',
      '/tickets/feed': 'shell.pageTitle.feed',
      '/tickets/canvas': 'shell.pageTitle.canvas',
      '/tickets/stories': 'shell.pageTitle.stories',
      '/tickets/epics': 'shell.pageTitle.epics',
      '/tickets/board': 'shell.pageTitle.board',
      '/tickets/standups': 'shell.pageTitle.standups',
      '/meet': 'shell.app.meet',
      '/system-status': 'shell.pageTitle.systemStatus',
      '/admin/system': 'shell.pageTitle.systemStatus',
      '/team-members': 'shell.app.teamMembers',
      '/people': 'shell.app.teamMembers'
    },
    NAVBAR_PREFIX: [
      ['/tickets/requests', 'shell.pageTitle.requests'],
      ['/tickets/resolutions', 'shell.pageTitle.resolutions'],
      ['/tasks', 'shell.app.tasks'],
      ['/task/', 'shell.app.tasks'],
      ['/tickets', 'shell.app.tasks'],
      ['/meet/', 'shell.pageTitle.call'],
      ['/notes/', 'shell.app.notes'],
      ['/notes', 'shell.app.notes'],
      // Legacy alias — documents→notes rename (2026-08-06 per Nika);
      // '/documents' stays reachable so a stale deep link still titles
      // correctly.
      ['/documents/', 'shell.app.notes'],
      ['/documents', 'shell.app.notes'],
      ['/studio', 'shell.app.studio'],
      ['/infra', 'shell.app.infra'],
      ['/admin', 'shell.pageTitle.admin']
    ],
    pageTitleFor(path) {
      if (!path) return 'shell.pageTitle.canvas'
      if (this.NAVBAR_TITLES[path]) return this.NAVBAR_TITLES[path]
      if (path.startsWith('/meet/') && path.endsWith('/transcript'))
        return 'shell.pageTitle.transcript'
      for (const [prefix, label] of this.NAVBAR_PREFIX) {
        if (path.startsWith(prefix)) return label
      }
      return 'shell.pageTitle.canvas'
    }
  },
  extends: 'IconNavbar',
  align: 'center space-around',
  gap: 'Z+V',

  hide: () =>
    /^\/(login|signin|signup|reset-password|forgot-password|verify-email|signup-confirmation)/.test(
      window.location.pathname
    ),

  // PageTitle ("Canvas" / "Marketplace" / etc.) renders to the right of
  // the icon row. The workspace switcher does NOT live here — it lives
  // in MainNavbar (`shared/components/MainNavbar.js:44`). The shell
  // suppresses AppNavbar via `AppNavbar: null` in `AppHeader.js:131`
  // and mounts MainNavbar in its place, so any slot declared here is
  // dead code at runtime. The previous `AppWorkspaceSwitcher: {}` slot
  // would never have rendered anyway because AppNavbar uses
  // `childrenAs: 'state' + children: apps` which silently overrides
  // named siblings.
  PageTitle: {
    tag: 'span',
    text: (el, s) => {
      const path =
        s.root?.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
      return el.call('polyglot', el.scope.pageTitleFor(path))
    },
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    padding: '0 Z'
  },

  childExtends: 'AppNavbarButton',
  childrenAs: 'state',
  // Feature-flag gate: items whose route maps to a flag (navItemVisible /
  // ROUTE_FLAGS) drop out when that feature is off at /flags. Unmapped
  // items (logo, System Status, More) always render.
  subscribeTo: ['_featureFlags', 'workspace'],
  children: (el, s) => el.scope.apps.filter((a) => navItemVisible(s.root, a))
}
